import { format, isBefore, isValid, parse } from 'date-fns';

import { extractDateOnly } from '~shared/helpers/datetime';

export function isValidTimeRange(startTime: string, endTime: string): boolean {
  // Define the expected time format, e.g., "08:00 AM"
  const timeFormat = 'HH:mm';
  const referenceDate = new Date(); // Use today's date as a reference

  // Parse the start and end times
  const start = parse(startTime, timeFormat, referenceDate);
  const end = parse(endTime, timeFormat, referenceDate);

  // Optionally, check that both times were parsed correctly
  if (!isValid(start) || !isValid(end)) {
    throw new Error('Invalid time format. Expected format is "HH:mm".');
  }

  // Validate that start time is strictly before end time
  return isBefore(start, end);
}

export function calculateDurationInMinutes(
  startTime?: string | null,
  endTime?: string | null,
): number {
  const trimmedStart = (startTime ?? '').trim();
  const trimmedEnd = (endTime ?? '').trim();
  if (!trimmedStart || !trimmedEnd) {
    return 0;
  }

  const parseTime = (time: string): number => {
    const timeComponents = time.split(':');
    if (timeComponents.length !== 2) {
      throw new Error(
        `Invalid time format: "${time}". Expected format "HH:mm".`,
      );
    }

    const hours = parseInt(timeComponents[0] ?? '0', 10);
    const minutes = parseInt(timeComponents[1] ?? '0', 10);

    if (isNaN(hours) || isNaN(minutes)) {
      throw new Error(`Invalid numeric values in time: "${time}".`);
    }

    return hours * 60 + minutes;
  };

  const startMinutes = parseTime(trimmedStart);
  const endMinutes = parseTime(trimmedEnd);

  return endMinutes - startMinutes;
}

/**
 * Normalizes a date string to 'yyyy-MM-dd' format if it contains a time component ('T').
 * If the date string does not contain 'T', it is returned as is.
 *
 * @param {string} dateStr - The date string to normalize.
 * @returns {string} The normalized date string in 'yyyy-MM-dd' format or the original string if no time component is present.
 */
export const normalizeDate = (dateStr: string) =>
  dateStr.includes('T') ? (extractDateOnly(dateStr) ?? dateStr) : dateStr;

/**
 * Normalizes a time string to the HH:mm format.
 * It attempts to parse the time string using 'hh:mm a' and 'HH:mm' formats.
 * If parsing is successful, it formats the parsed time to 'HH:mm'.
 * If parsing fails for both formats, it returns the original time string.
 *
 * @param {string} timeStr - The time string to normalize.
 * @returns {string} The normalized time string in 'HH:mm' format, or the original string if normalization fails.
 */
export const normalizeTime = (timeStr: string) => {
  if (!timeStr) {
    return timeStr;
  }

  if (timeStr.includes('T')) {
    const isoDate = new Date(timeStr);
    if (!Number.isNaN(isoDate.getTime())) {
      return format(isoDate, 'HH:mm');
    }
  }

  for (const formatStr of ['hh:mm a', 'HH:mm', 'MMM d, yyyy, h:mm:ss a']) {
    const parsedTime = parse(timeStr, formatStr, new Date());
    if (isValid(parsedTime)) return format(parsedTime, 'HH:mm');
  }
  return timeStr; // Return as is if invalid
};

/**
 * Formats a date and time string into a human-readable format.
 *
 * @param {string} date - The date string in 'yyyy-MM-dd' format or ISO format.
 * @param {string} time - The time string in 'HH:mm' format.
 * @returns {string} A formatted date and time string in 'MMM d, yyyy, h:mm:ss a' format, or 'Invalid Date' if the input is invalid.
 */
export const formatDateTime = (date: string, time: string) => {
  // Normalize the date to ensure it's in 'yyyy-MM-dd' format
  const normalizedDate = normalizeDate(date);

  const parsed = parse(
    `${normalizedDate} ${time}`,
    'yyyy-MM-dd HH:mm',
    new Date(),
  );
  return isValid(parsed)
    ? format(parsed, 'MMM d, yyyy, h:mm:ss a')
    : 'Invalid Date';
};

export const AGE_RANGE_REVERSED_MESSAGE =
  'Max age must be greater than or equal to min age.';

/**
 * Filters out the age-range message so it can be rendered once per age row
 * instead of under every age field.
 */
export const fieldErrorMessage = (message?: unknown): string | null =>
  typeof message === 'string' && message !== AGE_RANGE_REVERSED_MESSAGE
    ? message
    : null;

interface AgeFieldErrors {
  year?: { message?: unknown };
  month?: { message?: unknown };
}

export const hasAgeRangeError = (
  minAgeErrors?: AgeFieldErrors,
  maxAgeErrors?: AgeFieldErrors,
): boolean =>
  [
    minAgeErrors?.year?.message,
    minAgeErrors?.month?.message,
    maxAgeErrors?.year?.message,
    maxAgeErrors?.month?.message,
  ].some((message) => message === AGE_RANGE_REVERSED_MESSAGE);

interface AgeParts {
  year?: number | null;
  month?: number | null;
}

const toTotalMonths = (age?: AgeParts | null): number | null => {
  const year = age?.year;
  const month = age?.month;
  if (typeof year !== 'number' || Number.isNaN(year)) return null;
  const normalizedMonth =
    typeof month === 'number' && !Number.isNaN(month) ? month : 0;
  return year * 12 + normalizedMonth;
};

/**
 * Validates that a class min age is not greater than its max age.
 *
 * @returns `true` when the range is valid (or incomplete), otherwise the warning message.
 */
export const validateAgeRange = (
  minAge?: AgeParts | null,
  maxAge?: AgeParts | null,
): true | string => {
  const min = toTotalMonths(minAge);
  const max = toTotalMonths(maxAge);
  if (min === null || max === null) return true;
  return min <= max || AGE_RANGE_REVERSED_MESSAGE;
};
